import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Info, Loader2, ShieldCheck, TriangleAlert } from "lucide-react"
import { useEffect, useState } from "react"
import { useApprovePreview } from "../../../../../api/declarationsApi"
import { useNumberFormat } from "../../../../home/useNumberFormat"
import { BusyLabel } from "./BusyLabel"

/** How long the checking state may last before its description admits the wait
 *  is longer than usual (the final review's p90 is about 11 s). */
export const APPROVE_CHECK_SLOW_MS = 20_000

/** The counts the server names, one cell each, and the stagger their numbers
 *  arrive with. No sign between the cells: the verdict count follows approve's
 *  write rules and is not articles times regulations. */
const COUNT_CELLS = [
  {
    count: "verdicts",
    label: "MC_DECL_V2_APPROVE_CONFIRM_COUNT_VERDICTS",
    delay: "delay-0",
  },
  {
    count: "articles",
    label: "MC_DECL_V2_APPROVE_CONFIRM_COUNT_ARTICLES",
    delay: "delay-75",
  },
  {
    count: "regulations",
    label: "MC_DECL_V2_APPROVE_CONFIRM_COUNT_REGULATIONS",
    delay: "delay-150",
  },
] as const

/** The one panel that holds the counts and, while checking, the notice that
 *  takes their place. From `sm` up, where the counts stand side by side, it is
 *  as tall for the notice as for the counts, so neither that swap nor the slow
 *  copy's second line moves the buttons. */
const PANEL = "rounded-lg border border-grey4/60 bg-grey2 sm:min-h-[4.25rem]"

export interface ApproveConfirmDialogProps {
  open: boolean
  /** Whose write plan the dialog names (`useApprovePreview`). */
  declId: string
  onCancel: () => void
  onConfirm: () => void
  /** The approve request this dialog sent is in flight: the confirm button
   *  spins and the dialog cannot be closed, since closing it would not stop a
   *  request that is already out. */
  sending?: boolean
  /** An edit is still saving, the window the footer's own approve guard
   *  waits out: the confirm button spins until it lands rather than taking a
   *  click that guard would drop. */
  saving?: boolean
  /** The press was confirmed and the server refused it only because the run's
   *  final review is still going (`MC_APPROVE_PRECHECK`): the dialog stays up
   *  and says so while the footer's automatic retries wait for the review.
   *  Confirm cannot be pressed in this state; cancel ends the wait, except
   *  while a request is in flight (`sending`). */
  checking?: boolean
  /** How the pool-wide hint names a statement (MC_ISSUER_SCOPE_PROPOSAL):
   *  the card's own "Verordnung · Aussage N". Omitted, the Verordnung's slug. */
  describeStatement?: (framework: string, statementId: string) => string
}

/**
 * The last stop before approve writes its verdicts to master data and seals
 * the declaration, which cannot be undone (sandbox, 30.09.: one click on
 * "Approve all" wrote 28,403 verdicts for 7,834 articles). `StudioV2FooterBar`
 * opens it for EVERY approve press, the button and the 1 / Enter keys alike;
 * `OpenQuestionsAckDialog` comes first whenever it applies.
 *
 * The counts are the SERVER's (`useApprovePreview`), resolved through approve's
 * own write rules: frameworks sharing one regulation share a row, a framework
 * with no regulation writes none, held-back articles are not written. A count
 * summed over the studio's framework cells overstated all three. Confirm waits
 * for them; without them (an older backend, a failed read) the dialog still
 * says the approval is final and lets the reviewer go ahead, since a missing
 * preview must not block a valid approval.
 *
 * An AlertDialog, so focus lands on Cancel: an Enter pressed out of habit
 * right after the shortcut closes the dialog instead of approving, and
 * `useStudioV2Keyboard` stands down inside `role="alertdialog"`. Escape is
 * stopped from bubbling so it never also reaches `StudioV2Layout`'s
 * window-level back-to-list key (same as `OpenQuestionsAckDialog`).
 *
 * `checking` swaps the title and description for the wait's own copy and
 * leaves the buttons where they are, so the dialog does not move while the
 * footer retries; after `APPROVE_CHECK_SLOW_MS` in the state the description
 * says the wait is longer than usual.
 *
 * The counts stand in one panel, not in a sentence, and the line that the
 * approval is final stands on its own in every state. All of it sits inside
 * the description, so it is still what the dialog announces when it opens.
 * The numbers fade in when they arrive and never count up: every frame of a
 * confirmation that cannot be undone has to show the true count.
 */
export function ApproveConfirmDialog({
  open,
  declId,
  onCancel,
  onConfirm,
  sending = false,
  saving = false,
  checking = false,
  describeStatement = (framework) => framework,
}: ApproveConfirmDialogProps) {
  const { translate } = useTranslation()
  const { format } = useNumberFormat()
  const preview = useApprovePreview(declId, open)
  // Never the counts of an earlier open: an edit since then changed them.
  const loading = preview.isFetching
  const counts = loading ? undefined : preview.data
  const busy = sending || saving || loading || checking
  // MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): the scope_all statements approve would
  // apply to every article of a supplier carrying several manufacturers. A
  // hint, never a gate: confirm stays exactly as pressable as without it.
  const poolWideOn = useFeatureFlag("MC_ISSUER_SCOPE_PROPOSAL")
  const poolWide = poolWideOn ? (counts?.poolWideStatements ?? []) : []
  // Keyed on `checking` alone: a retry flipping `sending` must not restart the
  // clock, and leaving the state (or unmounting) clears it.
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!checking) return undefined
    const timer = setTimeout(() => setSlow(true), APPROVE_CHECK_SLOW_MS)
    return () => {
      clearTimeout(timer)
      setSlow(false)
    }
  }, [checking])

  const TitleIcon = checking ? Loader2 : ShieldCheck
  const checkingTitle = translate("MC_DECL_V2_APPROVE_CONFIRM_CHECKING_TITLE")
  const checkingDescription = translate(
    slow
      ? "MC_DECL_V2_APPROVE_CONFIRM_CHECKING_SLOW"
      : "MC_DECL_V2_APPROVE_CONFIRM_CHECKING_BODY",
  )

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !sending) onCancel()
      }}
    >
      <AlertDialogContent
        data-testid="approve-confirm-dialog"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        {/* The swap of title and description inside an open alertdialog is not
            announced by itself; this live region is, and is empty otherwise. */}
        <div
          role="status"
          className="sr-only"
          data-testid="approve-confirm-status"
        >
          {checking ? `${checkingTitle} ${checkingDescription}` : null}
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2.5">
            <TitleIcon
              aria-hidden="true"
              className={cn(
                "size-5 shrink-0 text-attention",
                checking && "animate-spin",
              )}
            />
            {checking
              ? checkingTitle
              : translate("MC_DECL_V2_APPROVE_CONFIRM_TITLE")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        {/* A div: the description's own `p` cannot hold the panel. `min-w-0`
            so a label too long for its cell wraps or truncates instead of
            widening the dialog's grid past the screen. */}
        <AlertDialogDescription asChild>
          <div className="flex min-w-0 flex-col gap-3">
            {!checking && (
              <p>
                {counts
                  ? translate("MC_DECL_V2_APPROVE_CONFIRM_LEAD")
                  : loading
                    ? translate("MC_DECL_V2_APPROVE_CONFIRM_LOADING")
                    : translate("MC_DECL_V2_APPROVE_CONFIRM_BODY_NO_COUNTS")}
              </p>
            )}
            {(checking || counts || loading) && (
              // The panel itself stays through every swap, only what it holds
              // changes. Below `sm` the dialog is narrower than three counts
              // need: one per row, count and label side by side.
              <div
                className={cn(
                  PANEL,
                  checking
                    ? "flex items-center px-4 py-2.5"
                    : "grid divide-y divide-grey4/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0",
                )}
              >
                {checking ? (
                  <p className="animate-in duration-200 fade-in motion-reduce:animate-none">
                    {checkingDescription}
                  </p>
                ) : (
                  COUNT_CELLS.map((cell) => (
                    <div
                      key={cell.count}
                      data-testid={`approve-confirm-count-${cell.count}`}
                      className="flex min-w-0 items-center px-4 py-2.5"
                    >
                      <div
                        className={cn(
                          "flex min-w-0 flex-1 items-baseline gap-2 sm:flex-col sm:items-stretch sm:gap-0.5",
                          counts &&
                            "animate-in duration-300 fill-mode-both fade-in slide-in-from-bottom-1 motion-reduce:animate-none",
                          counts && cell.delay,
                        )}
                      >
                        {counts ? (
                          <>
                            <span className="shrink-0 text-2xl/7 font-semibold text-foreground tabular-nums">
                              {format(counts[cell.count])}
                            </span>{" "}
                            <span className="min-w-0 text-xs break-words sm:truncate">
                              {translate(cell.label, {
                                count: counts[cell.count],
                              })}
                            </span>
                          </>
                        ) : (
                          <>
                            <Skeleton className="h-7 w-12" />
                            <Skeleton className="h-3 w-20 sm:mt-1" />
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {!checking && poolWide.length > 0 && (
              <div
                data-testid="approve-confirm-pool-wide"
                className="flex items-start gap-2 text-foreground"
              >
                <Info
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-attention"
                />
                <div className="min-w-0">
                  <p>
                    {translate(
                      counts?.poolGroupField === "product_group"
                        ? "MC_DECL_V2_APPROVE_CONFIRM_POOL_WIDE_GROUPS"
                        : "MC_DECL_V2_APPROVE_CONFIRM_POOL_WIDE",
                      {
                        count: poolWide.length,
                        n: format(counts?.poolSize ?? 0),
                      },
                    )}
                  </p>
                  <ul className="mt-1 list-disc pl-4">
                    {poolWide.map((s) => (
                      <li key={s.statementId} className="break-words">
                        {describeStatement(s.framework, s.statementId)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <p className="flex items-start gap-2 text-foreground">
              <TriangleAlert
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-warning"
              />
              {translate("MC_DECL_V2_APPROVE_CONFIRM_IRREVERSIBLE")}
            </p>
          </div>
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="approve-confirm-cancel"
            disabled={sending}
          >
            {translate("MC_DECL_V2_APPROVE_CONFIRM_CANCEL")}
          </AlertDialogCancel>
          <Button
            type="button"
            data-testid="approve-confirm"
            className="bg-attention text-black hover:bg-attention-strong"
            disabled={busy}
            onClick={onConfirm}
          >
            <BusyLabel busy={busy}>
              {translate("MC_DECL_V2_APPROVE_CONFIRM_ACTION")}
            </BusyLabel>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
